
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PositSummaryFullDetails from "@/components/posit/PositSummaryFullDetails";
import EditPositDialog from "@/components/posit/EditPositDialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import PositPlusLogo from "@/components/shared/PositPlusLogo";
import { slugify } from "@/utils/slug";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { usePositValidation } from "@/hooks/usePositValidation";

const PositDetails = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { positId: rawPositId } = useParams();

  // Validate posit ID and get clean version
  const { isValidPositId, positId } = usePositValidation(rawPositId);

  const { data: posit, isLoading } = useQuery({
    queryKey: ["posit-details", positId],
    queryFn: async () => {
      if (!user?.id || !positId) return null;
      
      const { data, error } = await supabase
        .from("posits")
        .select("summary_full, id, title")
        .eq("id", positId)
        .maybeSingle();

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load posit details",
          variant: "destructive",
        });
        throw error;
      }
      
      return data;
    },
    enabled: !!user?.id && !!positId && isValidPositId
  });

  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("users")
        .select("subscription_type")
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  const isPremium = userProfile?.subscription_type === 'posit_plus';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!posit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground">No posit found.</p>
        <Button onClick={() => navigate("/dashboard/posits")} className="mt-4">
          Back to Posits
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold">{posit.title}</h1>
          {/* Quality/Growth badges (placeholder until aggregates wired) */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">Quality: N/A</Badge>
            <Badge variant="outline" className="text-xs">Growth: N/A</Badge>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow-sm p-6">
          <PositSummaryFullDetails 
            summary={posit.summary_full} 
            searchTerm={searchTerm}
            isPremium={isPremium}
          />
        </div>
      </div>
    </div>
  );
};

export default PositDetails;
